import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { inventoryValuationApi } from "../../../api/inventory/valuation";
import { formatDecimal, osloMonthPeriod } from "../../../pages/dashboard/inventory/inventory-format";

export const InventoryStats = () => {
  const { t } = useTranslation();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const roles = useSelector((state) => state.session.currentUser?.roles || []);
  const canViewCost = roles.some((role) => ["admin", "inventory_finance"].includes(role.name));
  const [state, setState] = useState({ companyId: null, loading: true, data: null, error: false });

  useEffect(() => {
    if (!canViewCost || !companyId) return undefined;
    let active = true;
    const period = osloMonthPeriod();
    setState({ companyId, loading: true, data: null, error: false });
    inventoryValuationApi.getValuation({
      from: period.from,
      to: period.to,
      mode: "now",
      page: 1,
      per_page: 1,
    }).then((response) => {
      if (!active) return;
      if (!response?.data?.success || !response?.data?.data?.totals) throw new Error("Invalid valuation response");
      setState({ companyId, loading: false, data: response.data.data, error: false });
    }).catch(() => {
      if (active) setState({ companyId, loading: false, data: null, error: true });
    });
    return () => { active = false; };
  }, [canViewCost, companyId]);

  if (!canViewCost || !companyId) return null;
  const data = state.companyId === companyId ? state.data : null;
  const totals = data?.totals;
  const reliable = totals?.complete_value === true;
  const value = totals?.valued_amount == null ? null : formatDecimal(totals.valued_amount);
  return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6" aria-label={t("inventoryValuation.title")}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><h2 className="text-lg font-bold text-slate-900">{t("inventoryValuation.title")}</h2><p className="mt-1 text-sm text-slate-500">{t("inventoryValuation.currentPeriod")}</p></div>
      <Link to="/dashboard/inventory/valuation" className="inline-flex min-h-[44px] items-center rounded-xl border border-slate-200 px-4 text-sm font-semibold text-[#0B3A78] hover:bg-slate-50">{t("inventoryValuation.openReport")}</Link>
    </div>
    {(state.loading || state.companyId !== companyId) && <p role="status" className="mt-4 text-sm text-slate-500">{t("inventoryValuation.loading")}</p>}
    {state.companyId === companyId && state.error && <p role="alert" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-800">{t("inventoryValuation.loadFailed")}</p>}
    {totals && <div className="mt-4 grid gap-3 sm:grid-cols-3">
      <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">{t("inventoryValuation.closingQuantity")}</p><p className="mt-2 text-xl font-bold tabular-nums text-slate-900">{formatDecimal(totals.closing_quantity, 2) ?? "—"}</p></div>
      <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">{reliable ? t("inventoryValuation.totalValue") : t("inventoryValuation.valuedSubtotal")}</p><p className="mt-2 text-xl font-bold tabular-nums text-slate-900">{value == null ? "—" : `${value} ${data.currency || "NOK"}`}</p></div>
      <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">{t("inventoryValuation.unvaluedQuantity")}</p><p className="mt-2 text-xl font-bold tabular-nums text-amber-900">{formatDecimal(totals.unvalued_quantity, 2) ?? "—"}</p></div>
    </div>}
    {totals && !reliable && <p role="status" className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{data.coverage?.reliable_from ? t("inventoryValuation.incomplete", { date: data.coverage.reliable_from }) : t("inventoryValuation.openingMissing")}</p>}
  </section>;
};
